// src/components/SearchTypeSelection.jsx
import React from 'react';
import { Zap, Shield, Clock } from 'lucide-react';

const SEARCH_TYPES = [
  {
    type: 'username',
    icon: '👤',
    title: 'Username',
    desc: 'Scan 100+ social & dev platforms',
    badge: 'Live API',
    badgeClass: 'badge-teal',
    glow: 'rgba(20,184,166,0.12)',
  },
  {
    type: 'email',
    icon: '📧',
    title: 'Email',
    desc: 'Linked accounts & breach check',
    badge: 'OTP-Safe',
    badgeClass: 'badge-accent',
    glow: 'rgba(99,102,241,0.12)',
  },
  {
    type: 'phone',
    icon: '📞',
    title: 'Phone Number',
    desc: 'Carrier, region & spam database',
    badge: null,
    badgeClass: '',
    glow: 'rgba(251,146,60,0.10)',
  },
  {
    type: 'domain',
    icon: '🌐',
    title: 'Domain',
    desc: 'WHOIS, DNS & security analysis',
    badge: 'Live API',
    badgeClass: 'badge-teal',
    glow: 'rgba(56,189,248,0.10)',
  },
  {
    type: 'image',
    icon: '🖼️',
    title: 'Image / EXIF',
    desc: 'Reverse search & metadata forensics',
    badge: null,
    badgeClass: '',
    glow: 'rgba(192,132,252,0.10)',
  },
  {
    type: 'social',
    icon: '📱',
    title: 'Social Networks',
    desc: 'Deep-dive platform investigation',
    badge: null,
    badgeClass: '',
    glow: 'rgba(244,114,182,0.10)',
  },
  {
    type: 'ai_report',
    icon: '🧠',
    title: 'AI Web Intel',
    desc: 'Search the web & generate AI report',
    badge: 'AI',
    badgeClass: 'badge-teal',
    glow: 'rgba(168,85,247,0.12)',
  },
];

const STATS = [
  { icon: Zap,    value: '100+',       label: 'Platforms',      color: '#6ee7b7' },
  { icon: Clock,  value: 'Real-time',  label: 'Results',        color: '#a5b4fc' },
  { icon: Shield, value: 'OTP-Safe',   label: 'Email Scanning', color: '#f9a8d4' },
];

const SearchTypeSelection = ({ onSelectType }) => {
  return (
    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 860 }}>
      <div className="glass" style={{ borderRadius: 'var(--radius-xl)', padding: '48px 40px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 56, marginBottom: 16, lineHeight: 1 }}>🔍</div>
          <h1 style={{
            margin: 0,
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: '-0.03em',
          }}>
            <span className="gradient-text">OSINT Intelligence</span> Hub
          </h1>
          <p style={{ margin: '10px 0 0', color: 'var(--color-text-secondary)', fontSize: 15 }}>
            Comprehensive digital footprint investigation across 100+ platforms
          </p>
        </div>

        {/* Stats bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
          marginBottom: 36,
        }}>
          {STATS.map(({ icon: Icon, value, label, color }, i) => (
            <div
              key={label}
              className={`animate-fade-in-up stagger-${i + 1}`}
              style={{
                padding: '14px 18px',
                background: 'rgba(0,0,0,0.25)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                textAlign: 'center',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                <Icon size={16} color={color} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Search type cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14,
        }}>
          {SEARCH_TYPES.map((type, i) => (
            <div
              key={type.type}
              className={`animate-fade-in-up stagger-${i + 1}`}
              onClick={() => onSelectType(type.type)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onSelectType(type.type)}
              style={{
                padding: '22px 20px',
                borderRadius: 'var(--radius-lg)',
                background: `radial-gradient(ellipse at top left, ${type.glow}, transparent 70%), rgba(255,255,255,0.04)`,
                border: '1px solid var(--color-border)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 220ms var(--ease-out)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.borderColor = 'var(--color-border-active)';
                e.currentTarget.style.boxShadow = 'var(--shadow-card)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {type.badge && (
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                  <span className={`badge ${type.badgeClass}`}>{type.badge}</span>
                </div>
              )}
              <div style={{ fontSize: 40, marginBottom: 12 }}>{type.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 5 }}>{type.title}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{type.desc}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default SearchTypeSelection;
